import { withBase } from "./base.ts";

export class ApiFailure extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiFailure";
    this.status = status;
    this.code = code;
  }
}

// Headers every call of this page carries: the Studio's CSRF token on a portal session (setPageHeaders).
let pageHeaders: Record<string, string> = {};
export function setPageHeaders(headers: Record<string, string>): void {
  pageHeaders = { ...headers };
}

// headers: extra request headers (a secret that must not ride in a GET's address, like an Order again token).
export async function api<T>(path: string, init: { body?: unknown; headers?: Record<string, string> } = {}): Promise<T> {
  let res: Response;
  const headers = { ...pageHeaders, ...init.headers };
  try {
    res = await fetch(withBase(path), init.body === undefined
      ? (Object.keys(headers).length > 0 ? { headers } : {})
      : { method: "POST", headers: { "content-type": "application/json", ...headers }, body: JSON.stringify(init.body) });
  } catch {
    throw new ApiFailure(0, "offline", "We couldn't reach the server. Check your connection and try again.");
  }
  const data = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
  // Netlify's rate limiter answers 429 with no body of ours; a 429 we sent ourselves (send_limit) carries its own message.
  if (res.status === 429 && !data?.error?.message) throw new ApiFailure(429, "rate_limited", "Too many tries in a short time. Wait a minute and try again.");
  if (!res.ok) {
    throw new ApiFailure(res.status, data?.error?.code ?? "server_error", data?.error?.message ?? "Something went wrong. Please try again.");
  }
  return data as T;
}

// studioSession: Studio opened from the portal (Task A18); the session cookie names the shop and the role.
export type Scope = { client: string } | { fixToken: string } | { studioToken: string } | { studioSession: true } | { design: true };

export interface ProcessedLogo {
  logoId: string;
  widthPx: number;
  heightPx: number;
  backgroundDetected: boolean;
  backgroundRemoved: boolean;
  previewUrl: string;
}

// Downloads an image once and hands back a blob: URL, so it keeps showing after its signed link expires.
export async function hold(url: string): Promise<string> {
  try {
    const res = await fetch(url);
    if (!res.ok) return url;
    return URL.createObjectURL(await res.blob());
  } catch {
    return url;
  }
}

async function held(logo: ProcessedLogo): Promise<ProcessedLogo> {
  return { ...logo, previewUrl: await hold(logo.previewUrl) };
}

// extra goes into the logo-process body as it is, e.g. { gangSheet: true } on the designer.
export async function uploadLogo(scope: Scope, file: File, extra: Record<string, unknown> = {}): Promise<ProcessedLogo> {
  if (file.size > 25 * 1024 * 1024) throw new ApiFailure(400, "too_large", "Files must be 25 MB or smaller.");
  const ticket = await api<{ uploadId: string; url: string }>("/api/upload-ticket", { body: scope });
  const put = await fetch(ticket.url, { method: "PUT", headers: { "content-type": file.type || "application/octet-stream" }, body: file }).catch(() => null);
  if (!put?.ok) throw new ApiFailure(0, "upload_failed", "The upload did not finish. Check your connection and try again.");
  return held(await api<ProcessedLogo>("/api/logo-process", { body: { ...scope, uploadId: ticket.uploadId, removeBackground: false, ...extra } }));
}

export async function removeLogoBackground(scope: Scope, logoId: string): Promise<ProcessedLogo> {
  return held(await api<ProcessedLogo>("/api/logo-process", { body: { ...scope, fromLogoId: logoId, removeBackground: true } }));
}

// A new logo cut from one this page may use, in the pixels of its processed image (see web/lib/crop.ts).
export async function cropLogo(scope: Scope, logoId: string, crop: { x: number; y: number; w: number; h: number }): Promise<ProcessedLogo> {
  return held(await api<ProcessedLogo>("/api/logo-process", { body: { ...scope, fromLogoId: logoId, crop } }));
}
